const sharedHiddingTokens = [`.`, `{%`, ` as`, `@`];
const HIDING_RULES = {
  keyword: sharedHiddingTokens,
  column: sharedHiddingTokens, // This might hold more speicific words for hiding column suggestions
};

const FROM_CLAUSE = /\s+(from|join)\s+$/i;

const DEFINITION_CLAUSE = /\s+(from|join)\s+(\{\{ ?@)$/i;

const endsWithTableClause = (queryText: string): boolean => FROM_CLAUSE.test(queryText);

const endsWithDefinitionClause = (queryText: string): boolean => DEFINITION_CLAUSE.test(queryText);

function isTableCompletionNeedeed(queryText: string): boolean {
  return endsWithTableClause(queryText) || endsWithDefinitionClause(queryText);
}

function parseForAlias(queryText = ''): string {
  if (queryText.includes('.')) {
    const [, parsedText] = queryText.split('.');
    return parsedText;
  } else {
    return queryText;
  }
}

function getCompletionScore(algoliaSuggestion, suggestionIndex: number, totalSuggestions: number) {
  let matchMask: number, score: number;

  if (algoliaSuggestion.highlighted && algoliaSuggestion.highlighted.length) {
    let highlightsFromAlgolia = algoliaSuggestion.highlighted;
    let mask = 0;
    let startIndex = 0;
    let endIndex = 0;
    while (highlightsFromAlgolia.includes('<em>')) {
      startIndex = highlightsFromAlgolia.indexOf('<em>');
      highlightsFromAlgolia = highlightsFromAlgolia.slice(0, startIndex) + highlightsFromAlgolia.slice(startIndex + 4);
      endIndex = highlightsFromAlgolia.indexOf('</em>');
      highlightsFromAlgolia = highlightsFromAlgolia.slice(0, endIndex) + highlightsFromAlgolia.slice(endIndex + 5);
      for (let i = startIndex; i < endIndex; i++) {
        mask = mask | (1 << i);
      }
    }
    matchMask = mask;
  }
  score = totalSuggestions - suggestionIndex;

  return {
    matchMask,
    score,
  };
}

function getQueryText(session: AceAjax.IEditSession, pos: AceAjax.Position, prefix: string) {
  let token = session.getTextRange({
    start: { row: 0, column: 0 },
    end: pos,
  } as AceAjax.Range);
  token = token.substring(0, token.length - prefix.length);
  return token;
}

function isTableCompleter(completions) {
  return completions.some((completion) => ['table', 'definition'].includes(completion['suggestionType']));
}

function truncate(suggestion: string, limit = 25): string {
  if (suggestion.length > limit) {
    return `${suggestion.slice(0, limit - 3)}...`;
  } else {
    return suggestion;
  }
}

/**
 * @param  {string} queryText select * from t as
 * @param  {"column"|"keyword"="column"} hideFor
 * @returns boolean false => shows the suggestion hides otherwise
 */
function isQueryEndingWithSpecialWords(queryText: string, hideFor: 'column' | 'keyword' = 'column'): boolean {
  return isTableCompletionNeedeed(queryText) || HIDING_RULES[hideFor].some((token) => queryText.trim().endsWith(token));
}

function isWithinQuotes(cursorPosition: AceAjax.Position, document: AceAjax.Document): boolean {
  return document.getLine(cursorPosition.row)[cursorPosition.column] === "'";
}

function getCompleter() {
  return {
    insertMatch(editor: AceAjax.Editor, suggestion: AceAjax.Completion): void {
      if (editor['completer'].completions.filterText) {
        const ranges = editor.selection.getAllRanges();
        for (const range of ranges) {
          range.start.column -= editor['completer'].completions.filterText.length;
          editor.session.remove(range);
        }
      }
      editor.execCommand('insertstring', suggestion.replacement);
    },
  };
}

// Why Am I writing this??
// Definition completion happens in couple of different ways
// Just by typing definitionName e.g. below should show up autocomplete
// 1. select * from MyDefi
// 2. select * from @MyDefi
// 3. select * from {{@MyDef || {{ @MyDef
// conditional checks are to handle bracket completion behavior
// e.g. (select * from {{@YOU_ARE_TYPING_HERE}}) should complete definition
// without extra braces -> https://app.asana.com/0/0/891659497226542/f
function handleDefinitionReplacement(editor: AceAjax.Editor, definitionName: string, textToBeFiltered: string): void {
  const currentCursorPosition = editor.getCursorPosition();
  const currentLine = editor.session.getLine(currentCursorPosition.row);

  const [textBeforeFilter, textAfterFilter] = currentLine.split(textToBeFiltered);

  let filterTextFromLeft = 0;
  let filterTextFromRight = 0;

  if (textBeforeFilter.endsWith('{{ ')) {
    filterTextFromLeft = 3;
  } else if (textBeforeFilter.endsWith('{{')) {
    filterTextFromLeft = 2;
  }

  if (textAfterFilter.startsWith(' }}')) {
    // Ace matched the braces
    filterTextFromRight = 3;
  } else if (textAfterFilter.startsWith('}}')) {
    filterTextFromRight = 2;
  }

  const ranges = editor.selection.getAllRanges();
  for (const range of ranges) {
    range.start.column -= textToBeFiltered.length + filterTextFromLeft;
    range.end.column += filterTextFromRight;
    editor.session.remove(range);
  }

  editor.execCommand('insertstring', `{{ @${definitionName} }}`);
}

function isCursorWithinComment(session: AceAjax.IEditSession, cursorPosition: AceAjax.Position) {
  const { row, column } = cursorPosition;
  const { type } = <any>session.getTokenAt(row, column) || { type: '' };
  return type === 'comment';
}

function isCompletionNeeded(
  session: AceAjax.IEditSession,
  cursorPosition: AceAjax.Position,
  prefix: string,
  completionFor: 'column' | 'keyword' = 'column'
): boolean {
  const queryText = getQueryText(session, cursorPosition, prefix).toLowerCase();

  return (
    !isQueryEndingWithSpecialWords(queryText, completionFor) &&
    !isWithinQuotes(cursorPosition, session.doc) &&
    !isCursorWithinComment(session, cursorPosition)
  );
}

export default {
  getQueryText,
  getCompletionScore,
  isTableCompletionNeedeed,
  parseForAlias,
  isTableCompleter,
  truncate,
  isQueryEndingWithSpecialWords,
  getCompleter,
  isWithinQuotes,
  isCompletionNeeded,
  endsWithDefinitionClause,
  isCursorWithinComment,
  handleDefinitionReplacement,
};
